import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useLazyGetRouteDepartureETAQuery } from "store/services/publicApi";
import { LandingFooter } from "./LandingFooter";
import NavBar from "components/navbar";

const RouteRoutineDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get routeId from location state (passed from RouteRoutineList)
    const routeId = location.state?.routeId;

    const [fetchETA, { data: etaData, isLoading }] = useLazyGetRouteDepartureETAQuery();

    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    useEffect(() => {
        // If no routeId in state, redirect back to list
        if (!routeId) {
            navigate('/routine-list');
            return;
        }

        // Fetch ETA data using routeId from state
        fetchETA(routeId);
    }, [routeId, fetchETA, navigate]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!etaData) {
        return (
            <Box p={3}>
                <Typography>No data available</Typography>
            </Box>
        );
    }

    const { stops = [], vehicleCount = 0, schedule = {} } = etaData;

    return (
        <>
            <NavBar />
            <Box p={3} sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        sx={{
                            mr: 2,
                            backgroundColor: "crimson",
                            color: "white",
                            "&:hover": { backgroundColor: "darkred" },
                        }}
                    >
                        Back
                    </Button>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {etaData?.routeName || "Route Schedule"}
                    </Typography>
                </Box>

                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                    <Typography variant="body1">
                        <strong>Route:</strong> {etaData?.origin} → {etaData?.destination}
                    </Typography>
                </Paper>

                <TableContainer component={Paper} elevation={2}>
                    <Table sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
                        <TableHead sx={{ backgroundColor: "crimson" }}>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: "bold",
                                        minWidth: 180,
                                        color: "white",
                                        fontSize: "0.95rem",
                                        py: 1.5,
                                        borderRight: "2px solid white",
                                    }}
                                >
                                    Stop Information
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: "bold",
                                        minWidth: 120,
                                        color: "white",
                                        fontSize: "0.95rem",
                                        py: 1.5,
                                        borderRight: "2px solid white",
                                    }}
                                >
                                    Vehicle
                                </TableCell>
                                {daysOfWeek.map((day, idx) => (
                                    <TableCell
                                        key={day}
                                        sx={{
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "white",
                                            fontSize: "0.95rem",
                                            py: 1.5,
                                            minWidth: 120,
                                            borderRight: idx < daysOfWeek.length - 1 ? "2px solid white" : "none",
                                        }}
                                    >
                                        {day}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Departure Time Rows (Origin) */}
                            {[...Array(vehicleCount)].map((_, vIdx) => (
                                <TableRow
                                    key={`departure-v${vIdx}`}
                                    sx={{
                                        backgroundColor: "#556B2F",
                                        borderBottom: vIdx < vehicleCount - 1 ? "1px solid #ddd" : "2px solid #ddd"
                                    }}
                                >
                                    {vIdx === 0 && (
                                        <TableCell
                                            rowSpan={vehicleCount}
                                            sx={{
                                                fontWeight: 600,
                                                borderLeft: "4px solid #6B8E23",
                                                borderRight: "2px solid #ddd",
                                                py: 1.5,
                                                verticalAlign: "middle"
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.8)", mb: 0.3 }}>
                                                Departure from:
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: "white", fontWeight: 600, fontSize: "0.95rem" }}>
                                                {etaData?.origin}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell sx={{
                                        fontWeight: 600,
                                        borderRight: "2px solid #ddd",
                                        py: 1.5,
                                        backgroundColor: vIdx % 2 === 0 ? "#e3f2fd" : "#fff3e0",
                                    }}>
                                        <Typography variant="body2" sx={{ fontSize: "0.85rem", fontWeight: 600, color: vIdx % 2 === 0 ? "#1565c0" : "#e65100" }}>
                                            Vehicle {vIdx + 1}
                                        </Typography>
                                    </TableCell>
                                    {daysOfWeek.map((day, dayIdx) => {
                                        const time = schedule[day]?.["-1"]?.[vIdx];

                                        return (
                                            <TableCell
                                                key={day}
                                                sx={{
                                                    textAlign: "center",
                                                    verticalAlign: "middle",
                                                    py: 1.5,
                                                    px: 1,
                                                    backgroundColor: vIdx % 2 === 0 ? "#e3f2fd" : "#fff3e0",
                                                    borderRight: dayIdx < daysOfWeek.length - 1 ? "2px solid #ddd" : "none",
                                                }}
                                            >
                                                {time ? (
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: vIdx % 2 === 0 ? "#1565c0" : "#e65100",
                                                            fontWeight: 700,
                                                            fontSize: "0.95rem",
                                                        }}
                                                    >
                                                        {time}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}

                            {/* Arrow after departure */}
                            <TableRow>
                                <TableCell
                                    sx={{
                                        textAlign: "center",
                                        py: 0,
                                        px: 0,
                                        backgroundColor: "transparent",
                                        borderBottom: "none",
                                        borderTop: "none",
                                        borderLeft: "4px solid transparent"
                                    }}
                                >
                                    <Typography sx={{ fontSize: "1.2rem", color: "#666", lineHeight: 1 }}>
                                        ↓
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    colSpan={daysOfWeek.length + 1}
                                    sx={{
                                        borderBottom: "none",
                                        borderTop: "none",
                                        py: 0,
                                        backgroundColor: "transparent"
                                    }}
                                />
                            </TableRow>

                            {/* Stop Rows */}
                            {stops.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={daysOfWeek.length + 2}
                                        align="center"
                                        sx={{ py: 5 }}
                                    >
                                        <Typography color="textSecondary">
                                            No stops available for this route.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stops.map((stop, stopIndex) => (
                                    <React.Fragment key={stopIndex}>
                                        {/* Arrow Row before this stop */}
                                        {stopIndex > 0 && (
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        textAlign: "center",
                                                        py: 0,
                                                        px: 0,
                                                        backgroundColor: "transparent",
                                                        borderBottom: "none",
                                                        borderTop: "none",
                                                        borderLeft: "4px solid transparent"
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: "1.2rem", color: "#666", lineHeight: 1 }}>
                                                        ↓
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    colSpan={daysOfWeek.length + 1}
                                                    sx={{
                                                        borderBottom: "none",
                                                        borderTop: "none",
                                                        py: 0,
                                                        backgroundColor: "transparent"
                                                    }}
                                                />
                                            </TableRow>
                                        )}
                                        {/* Vehicle rows for this stop */}
                                        {[...Array(vehicleCount)].map((_, vIdx) => (
                                            <TableRow
                                                key={`stop-${stopIndex}-v${vIdx}`}
                                                sx={{
                                                    "&:hover": { backgroundColor: "#f0f7ff" },
                                                    transition: "background-color 0.2s",
                                                    borderBottom: vIdx < vehicleCount - 1 ? "1px solid #ddd" : "2px solid #ddd"
                                                }}
                                            >
                                                {vIdx === 0 && (
                                                    <TableCell
                                                        rowSpan={vehicleCount}
                                                        sx={{
                                                            fontWeight: 600,
                                                            borderLeft: "4px solid #6B8E23",
                                                            borderRight: "2px solid #ddd",
                                                            backgroundColor: "#556B2F",
                                                            py: 1.5,
                                                            verticalAlign: "middle"
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.8)", mb: 0.3 }}>
                                                            Arrives at:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: "white", fontWeight: 600, fontSize: "0.95rem" }}>
                                                            {stop.name}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    borderRight: "2px solid #ddd",
                                                    py: 1.5,
                                                    backgroundColor: vIdx % 2 === 0 ? "#e3f2fd" : "#fff3e0",
                                                }}>
                                                    <Typography variant="body2" sx={{
                                                        fontSize: "0.85rem",
                                                        fontWeight: 600,
                                                        color: vIdx % 2 === 0 ? "#1565c0" : "#e65100"
                                                    }}>
                                                        Vehicle {vIdx + 1}
                                                    </Typography>
                                                </TableCell>
                                                {daysOfWeek.map((day, dayIdx) => {
                                                    const time = schedule[day]?.[stopIndex]?.[vIdx];

                                                    return (
                                                        <TableCell
                                                            key={day}
                                                            sx={{
                                                                textAlign: "center",
                                                                verticalAlign: "middle",
                                                                py: 1.5,
                                                                px: 1,
                                                                backgroundColor: vIdx % 2 === 0 ? "#e3f2fd" : "#fff3e0",
                                                                borderRight: dayIdx < daysOfWeek.length - 1 ? "2px solid #ddd" : "none",
                                                            }}
                                                        >
                                                            {time ? (
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{
                                                                        color: vIdx % 2 === 0 ? "#1565c0" : "#e65100",
                                                                        fontWeight: 700,
                                                                        fontSize: "0.95rem",
                                                                    }}
                                                                >
                                                                    {time}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <LandingFooter />
        </>
    );
};

export default RouteRoutineDetail;